import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { TotalResult } from '../../../Surveys/util/SurveyResponseResults';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Box } from '@lifeomic/chroma-react/components/Box';
import ResponseLineItem from './ResponseLineItem';
import SurveyTotals from './SurveyTotals';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import SurveyResponse from '../../../Surveys/util/SurveyResponse';

const messages = defineMessages({
  selectResponseMessage: {
    id: 'SurveyResults.selectResponseMessage',
    defaultMessage: 'Select a survey and response to view response data',
  },
});

export const useStyles = makeStyles((theme: Theme) => ({
  bkgd: {
    background: theme.palette.black[50],
    padding: theme.spacing(2),
    width: '100%',
  },
  root: {
    background: theme.palette.common.white,
    borderRadius: theme.pxToRem(4),
    boxShadow: theme.boxShadows.table,
    flex: 1,
    minWidth: 0,
    height: '100%',
    overflow: 'auto',
    flexDirection: 'column',
  },
  tightTableCell: {
    whiteSpace: 'normal',
  },
  scoringTableCell: {
    paddingLeft: theme.spacing(4),
    fontWeight: 'bolder',
    textAlign: 'right',
  },
  divider: {
    margin: 0,
  },
}));

export type ResultsTableOwnProps = {
  hasScoring: boolean;
  totalResult: TotalResult;
  selectedResponse?: SurveyResponse;
  onFavoriteUpdate?: (resource: fhir.QuestionnaireResponse) => void;
};

export type ResultsTableProps = ResultsTableOwnProps;

const ResultsTable: React.FC<ResultsTableProps> = ({
  totalResult,
  hasScoring,
  selectedResponse,
  onFavoriteUpdate,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});

  const response = selectedResponse?.toFhirQuestionnaireResponse();

  if (!totalResult?.lineItems) {
    return (
      <Box
        fullWidth
        fullHeight
        justify="center"
        align="center"
        className={classes.root}
      >
        <Text size="headline" weight="bold">
          {formatMessage(messages.selectResponseMessage)}
        </Text>
      </Box>
    );
  } else {
    return (
      <Box className={classes.bkgd}>
        <Box className={classes.root}>
          {totalResult.lineItems.map(
            (lineItem, index) =>
              !!response && (
                <ResponseLineItem
                  key={index}
                  lineItem={lineItem}
                  parentHasScoring={hasScoring}
                  selectedResponse={response}
                  onFavoriteUpdate={onFavoriteUpdate}
                />
              ),
          )}
          <Divider className={classes.divider} />
          {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
          <SurveyTotals surveyTotal={totalResult.surveyTotal} />
        </Box>
      </Box>
    );
  }
};

export default React.memo(ResultsTable);
